import api, { ApiResponse } from './api';

export interface Building {
  id: number;
  name: string;
  street_name: string;
  street_number: string;
  full_address: string | null;
  city: string;
  state: string;
  zip: string;
  email: string | null;
  phone: string | null;
  image_url: string | null;
  building_image: string | null;
  status: number;
  is_hide: number | null;
  require_escort_photo?: boolean;
  created_at: string;
  updated_at: string;
}

// Helper function to format building address with fallback to full_address
export function formatBuildingAddress(building: Building): string {
  // If individual address components exist, use them
  if (building.street_number && building.street_name) {
    const parts = [`${building.street_number} ${building.street_name}`];
    if (building.city) parts.push(building.city);
    if (building.state) parts.push(building.state);
    return parts.join(', ');
  }

  // Fallback to full_address
  if (building.full_address) {
    return building.full_address;
  }

  // Last resort - try to construct from whatever is available
  const parts: string[] = [];
  if (building.street_number) parts.push(building.street_number);
  if (building.street_name) parts.push(building.street_name);
  if (building.city) parts.push(building.city);
  if (building.state) parts.push(building.state);

  return parts.join(', ') || 'Address not available';
}

export interface SiteManager {
  id: number;
  name: string;
  email: string | null;
  username: string;
  image: string | null;
  user_type: number;
  user_status: number;
  package_handled: number;
}

export interface FloorRepresentative {
  id: number;
  name: string;
  email: string | null;
  username: string;
  image: string | null;
  user_type: number;
  user_status: number;
  package_handled: number;
}

interface FloorRepsResponse {
  data: FloorRepresentative[];
  next_page: number;
}

export const buildingService = {
  async getAuthorizedBuildings(): Promise<Building[]> {
    const response = await api.get<ApiResponse<Building[]>>('/me/buildings', { timeout: 15000 });

    if (response.data.CODE === 200) {
      return response.data.RESULT;
    }

    throw new Error(response.data.MESSAGE || 'Failed to fetch assigned buildings');
  },

  async getBuildings(): Promise<Building[]> {
    const response = await api.get<ApiResponse<Building[]>>('/manage-building-web', { timeout: 15000 });

    if (response.data.CODE === 200) {
      return response.data.RESULT;
    }

    throw new Error(response.data.MESSAGE || 'Failed to fetch buildings');
  },

  async getBuilding(id: number): Promise<Building> {
    // Laravel doesn't have a single building endpoint, fetch all and filter
    const buildings = await this.getBuildings();
    const building = buildings.find(b => b.id === id);

    if (building) {
      return building;
    }

    throw new Error('Building not found');
  },

  async getSiteManagers(buildingId: number): Promise<SiteManager[]> {
    // Site managers come from the same /manage-user endpoint but with user_type = 1
    const response = await api.get<ApiResponse<FloorRepsResponse>>(`/manage-user?building_id=${buildingId}`);

    // CODE 200 = success with data, CODE 450 = no users found (empty result)
    if (response.data.CODE === 200 || response.data.CODE === 450) {
      const result = response.data.RESULT;
      if (result && Array.isArray(result.data)) {
        // Filter for user_type = 1 (Site Manager)
        return result.data.filter(user => user.user_type === 1) as SiteManager[];
      }
      return [];
    }

    throw new Error(response.data.MESSAGE || 'Failed to fetch site managers');
  },

  async getFloorReps(buildingId: number): Promise<FloorRepresentative[]> {
    const response = await api.get<ApiResponse<FloorRepsResponse>>(`/manage-user?building_id=${buildingId}`);

    // CODE 200 = success with data, CODE 450 = no floor reps found (empty result)
    if (response.data.CODE === 200 || response.data.CODE === 450) {
      const result = response.data.RESULT;
      // API returns { data: [...], next_page: number }
      if (result && Array.isArray(result.data)) {
        // Filter for user_type = 2 (Floor Representative)
        return result.data.filter(user => user.user_type === 2);
      }
      return [];
    }

    throw new Error(response.data.MESSAGE || 'Failed to fetch floor representatives');
  },

  async createBuilding(data: Partial<Building>): Promise<Building> {
    const response = await api.post<ApiResponse<Building>>('/manage-building', data);

    if (response.data.CODE === 200) {
      return response.data.RESULT;
    }

    throw new Error(response.data.MESSAGE || 'Failed to create building');
  },

  async updateBuilding(id: number, data: Partial<Building> & { phone_number?: string }): Promise<Building> {
    const response = await api.put<ApiResponse<Building>>(`/manage-building/${id}`, data);

    if (response.data.CODE === 200) {
      return response.data.RESULT;
    }

    throw new Error(response.data.MESSAGE || 'Failed to update building');
  },

  async updateBuildingStatus(id: number, status: number): Promise<Building> {
    const response = await api.post<ApiResponse<Building>>('/manage-building/update-status', { id, status });

    if (response.data.CODE === 200) {
      return response.data.RESULT;
    }

    throw new Error(response.data.MESSAGE || 'Failed to update building status');
  },

  async archiveBuilding(id: number): Promise<Building> {
    return this.updateBuildingStatus(id, 0); // status 0 = archived (also sets is_hide)
  },

  async activateBuilding(id: number): Promise<Building> {
    return this.updateBuildingStatus(id, 1); // status 1 = active (also clears is_hide)
  },

  async uploadBuildingImage(id: number, file: File): Promise<Building> {
    const formData = new FormData();
    formData.append('image', file);

    const response = await api.post<ApiResponse<Building>>(`/manage-building/${id}/image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    if (response.data.CODE === 200) {
      return response.data.RESULT;
    }

    throw new Error(response.data.MESSAGE || 'Failed to upload building image');
  },
};

export default buildingService;
